import { useQuery } from "@tanstack/react-query";
import {
  DEFAULT_FRANCHISE_SETTINGS,
  FRANCHISE_ROUTES,
  franchisePricesOf,
  responseSchema,
  type Currency,
} from "@nh/shared";
import { z } from "zod";
import { apiFetch } from "@/shared/api/client";

/**
 * Precio y vigencia de la franquicia, los que Anahí pone en
 * Admin → Franquicias (14-sep-2026: «esos datos deben afectar los precios
 * que se muestran al público y los links de pago»).
 *
 * Antes la landing pintaba la CONSTANTE del contrato y el admin cambiaba
 * un número que nadie veía. Ahora los dos lados leen la misma fila:
 * el servidor para cobrar, aquí para enseñar. La constante queda solo
 * como valor mientras carga — que es la semilla de esa fila, así que en
 * el peor caso se pinta el precio de fábrica un instante.
 */
const publicSettingsSchema = responseSchema(
  z.object({
    settings: z.object({
      priceMXNCents: z.number().int().min(0),
      priceUSDCents: z.number().int().min(0),
      durationMonths: z.number().int().min(1),
    }),
  }),
);

export interface FranchiseSettingsView {
  prices: Readonly<Record<Currency, number>>;
  durationMonths: number;
  /** `true` mientras se pinta la semilla y no lo guardado. */
  isFallback: boolean;
}

export function useFranchiseSettings(): FranchiseSettingsView {
  const query = useQuery({
    queryKey: ["franchise-settings", "public"],
    queryFn: async () =>
      (await apiFetch(FRANCHISE_ROUTES.publicSettings, publicSettingsSchema, { baseUrl: "" })).settings,
    staleTime: 60_000,
  });
  const settings = query.data ?? DEFAULT_FRANCHISE_SETTINGS;
  return {
    prices: franchisePricesOf(settings),
    durationMonths: settings.durationMonths,
    isFallback: query.data === undefined,
  };
}
